// useDebouncedBackgroundSave.js - Combines debouncing with background save queue
import { useState, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import saveQueueService from '../services/saveQueueService';

/**
 * useDebouncedBackgroundSave Hook
 * Combines debouncing (to reduce API calls) with background saving (to prevent data loss)
 * 
 * @param {Object} options
 * @param {Function} options.onSave - Async function to save data
 * @param {string} options.taskId - The task ID being edited
 * @param {number} options.debounceMs - Debounce delay in milliseconds
 * @param {Function} options.onError - Error handler callback
 * @param {Function} options.onSuccess - Success handler callback
 */
export const useDebouncedBackgroundSave = ({
  onSave,
  taskId,
  debounceMs = 500,
  onError,
  onSuccess
}) => {
  // Track field save status
  const [fieldSaveStatus, setFieldSaveStatus] = useState({});
  const [queueStatus, setQueueStatus] = useState({ queue: [], processing: false });
  
  // Track pending changes that haven't been queued yet
  const pendingChanges = useRef({});
  
  // Track mounted state
  const isMounted = useRef(true);
  
  // Create a stable save function
  const stableSaveFunction = useRef(onSave);
  useEffect(() => {
    stableSaveFunction.current = onSave;
  }, [onSave]);

  // Listen to queue changes
  useEffect(() => {
    const handleQueueChange = (status) => {
      if (isMounted.current) {
        setQueueStatus(status);
        
        // Update field statuses based on queue
        const newFieldStatus = {};
        status.queue.forEach(item => {
          if (item.taskId === taskId) {
            if (item.status === 'processing') {
              newFieldStatus[item.field] = 'saving';
            } else if (item.status === 'retrying') {
              newFieldStatus[item.field] = 'retrying';
            } else if (item.status === 'failed') {
              newFieldStatus[item.field] = 'error';
            } else {
              newFieldStatus[item.field] = 'pending';
            }
          }
        });
        
        setFieldSaveStatus(newFieldStatus);
      }
    };

    saveQueueService.addListener(handleQueueChange);
    
    return () => {
      saveQueueService.removeListener(handleQueueChange);
    };
  }, [taskId]);

  // Mark component as unmounted on cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Flush any pending debounced saves when unmounting
      flushPendingSaves();
    };
  }, []);


  // Store debounced functions per field
  const debouncedSaveRefs = useRef({});
  
  // Get or create debounced save function for a field
  const getDebouncedSave = useCallback((field) => {
    if (!debouncedSaveRefs.current[field]) {
      debouncedSaveRefs.current[field] = debounce(
        (value) => {
          // Remove from pending changes
          delete pendingChanges.current[field];
          
          // Capture the taskId at the time of queueing
          const capturedTaskId = taskId;
          
          // Create queue function with captured taskId
          const queueFunction = async (field, value) => {
            // Pass the captured taskId to the save function
            return stableSaveFunction.current(field, value, capturedTaskId);
          };
          
          // Add to background queue with captured taskId
          saveQueueService.enqueue(capturedTaskId, field, value, queueFunction);
        },
        debounceMs
      );
    }
    return debouncedSaveRefs.current[field];
  }, [taskId, debounceMs]);

  // Flush all pending debounced saves immediately
  const flushPendingSaves = useCallback(() => {
    // Flush all debounced functions
    Object.entries(debouncedSaveRefs.current).forEach(([field, debouncedFn]) => {
      if (debouncedFn && debouncedFn.flush) {
        debouncedFn.flush();
      }
    });
    
    // Queue any remaining pending changes
    Object.entries(pendingChanges.current).forEach(([field, value]) => {
      // Capture the taskId at the time of flushing
      const capturedTaskId = taskId;
      
      // Create queue function with captured taskId
      const queueFunction = async (field, value) => {
        // Pass the captured taskId to the save function
        return stableSaveFunction.current(field, value, capturedTaskId);
      };
      
      saveQueueService.enqueue(capturedTaskId, field, value, queueFunction);
    });
    
    // Clear pending changes
    pendingChanges.current = {};
  }, [taskId]);

  // Trigger save with debouncing
  const triggerSave = useCallback((field, value) => {
    if (!taskId) {
      return;
    }
    
    // Track as pending
    pendingChanges.current[field] = value;
    
    // Mark field as pending immediately
    setFieldSaveStatus(prev => ({ ...prev, [field]: 'pending' }));
    
    // Get or create debounced save for this field
    const debouncedSave = getDebouncedSave(field);
    
    // Trigger the debounced save
    debouncedSave(value);
  }, [taskId, getDebouncedSave]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // Check both pending changes and queue
    return Object.keys(pendingChanges.current).length > 0 || 
           saveQueueService.hasPendingForTask(taskId);
  }, [taskId]);

  // Get field status
  const getFieldStatus = useCallback((field) => {
    return fieldSaveStatus[field] || 'idle';
  }, [fieldSaveStatus]);

  // Get queue info for this task
  const getQueueInfo = useCallback(() => {
    return {
      pending: saveQueueService.getPendingForTask(taskId),
      hasPending: saveQueueService.hasPendingForTask(taskId),
      pendingChanges: { ...pendingChanges.current }
    };
  }, [taskId]);

  return {
    // Status getters
    fieldSaveStatus,
    hasUnsavedChanges: hasUnsavedChanges(),
    queueStatus,
    
    // Actions
    triggerSave,
    flushPendingSaves,
    getFieldStatus,
    getQueueInfo
  };
};